import { Link } from 'react-router-dom'
import { Check } from 'lucide-react'
import { PHONE_DISPLAY, PHONE_TEL, NON_MEDICAL_DISCLAIMER } from '../lib/constants'

/** Section wrapper. tone: 'cream' | 'panel' | 'panel-deep' | 'dark' | 'white' */
export function Section({ tone = 'cream', hero = false, children, ...rest }) {
  const bg = {
    cream: 'var(--color-cream)',
    panel: 'var(--color-panel)',
    'panel-deep': 'var(--color-panel-deep)',
    dark: 'var(--color-teal-900)',
    white: '#FFFFFF',
  }[tone]
  return (
    <section
      className={hero ? 'section-hero' : 'section'}
      style={{ background: bg, color: tone === 'dark' ? 'var(--color-cream)' : undefined }}
      {...rest}
    >
      <div className="container-site">{children}</div>
    </section>
  )
}

/** Eyebrow + H2 + optional lede, constrained to 768px. */
export function SectionHeading({ eyebrow, title, lede, onDark = false }) {
  return (
    <div style={{ maxWidth: 768 }}>
      {eyebrow && <p className={onDark ? 'eyebrow eyebrow-on-dark' : 'eyebrow'}>{eyebrow}</p>}
      <h2 className="h2-section" style={{ marginTop: 12, color: onDark ? 'var(--color-cream)' : undefined }}>
        {title}
      </h2>
      {lede && (
        <p
          style={{
            margin: '20px 0 0 0',
            fontSize: 19,
            lineHeight: 1.7,
            color: onDark ? 'oklch(0.9765 0.0098 87.47 / 85%)' : 'var(--color-muted)',
          }}
        >
          {lede}
        </p>
      )}
    </div>
  )
}

/** 52px icon circle. variant: 'on-teal' (cream/15% bg, cream glyph) | 'sage' (sage/20% bg, sage-deep glyph) */
export function IconCircle({ icon: Icon, variant = 'sage', size = 52 }) {
  const onTeal = variant === 'on-teal'
  return (
    <span
      style={{
        display: 'flex',
        height: size,
        width: size,
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        background: onTeal ? 'oklch(0.9765 0.0098 87.47 / 15%)' : 'oklch(0.6058 0.0491 154.68 / 20%)',
      }}
    >
      <Icon size={size >= 52 ? 24 : 16} color={onTeal ? 'var(--color-cream)' : 'var(--color-sage-deep)'} />
    </span>
  )
}

/** Numbered circle used for steps / pillars. */
export function NumberCircle({ n, onDark = false, size = 48 }) {
  return (
    <span
      style={{
        display: 'flex',
        height: size,
        width: size,
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        background: onDark ? 'oklch(0.9765 0.0098 87.47 / 15%)' : 'oklch(0.6058 0.0491 154.68 / 25%)',
        fontFamily: 'var(--font-sans)',
        fontSize: 18,
        fontWeight: 600,
        color: onDark ? 'var(--color-cream)' : 'var(--color-teal-900)',
      }}
    >
      {n}
    </span>
  )
}

/** Green check row used in checklists. */
export function CheckItem({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <span
        style={{
          marginTop: 4,
          display: 'flex',
          height: 28,
          width: 28,
          flexShrink: 0,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          background: 'oklch(0.6058 0.0491 154.68 / 20%)',
        }}
      >
        <Check size={16} color="var(--color-sage-deep)" />
      </span>
      <span style={{ fontSize: 17 }}>{children}</span>
    </div>
  )
}

/** FAQ accordion using native <details>. items: [{q, a}] */
export function FAQ({ eyebrow = 'Common questions', title = 'Questions families ask us', items }) {
  return (
    <>
      <SectionHeading eyebrow={eyebrow} title={title} />
      <div style={{ marginTop: 40, maxWidth: 768 }}>
        {items.map((f) => (
          <details key={f.q} style={{ borderBottom: '1px solid var(--color-line)' }}>
            {/* The question is an h3 so each FAQ sits properly under the
                section's h2 in the document outline. */}
            <summary style={{ padding: '24px 0' }}>
              <h3
                style={{
                  display: 'inline',
                  margin: 0,
                  fontFamily: 'var(--font-serif)',
                  fontSize: 22,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  letterSpacing: 'normal',
                  color: 'var(--color-teal-900)',
                }}
              >
                {f.q}
              </h3>
            </summary>
            <p style={{ margin: 0, padding: '0 0 24px 0', fontSize: 17, lineHeight: 1.6, color: 'var(--color-muted)' }}>
              {f.a}
            </p>
          </details>
        ))}
      </div>
    </>
  )
}

/** Teal CTA band at the bottom of every marketing page. */
export function CTABand({
  eyebrow = 'Next step',
  title = 'Ready to talk through care options?',
  lede = "Tell us what's happening at home and we'll walk you through the next step — no pressure, no obligation.",
  primaryLabel = 'Schedule an In-Home Assessment',
  primaryTo = '/schedule-assessment',
  secondaryLabel,
  secondaryTo,
}) {
  return (
    <Section tone="dark">
      <SectionHeading eyebrow={eyebrow} title={title} lede={lede} onDark />
      <div style={{ marginTop: 32, display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        <Link to={primaryTo} className="btn btn-on-dark">
          {primaryLabel}
        </Link>
        {/* Falls back to the phone number unless a page supplies its own second action. */}
        {secondaryLabel && secondaryTo ? (
          <Link to={secondaryTo} className="btn btn-outline-on-dark">
            {secondaryLabel}
          </Link>
        ) : (
          <a href={PHONE_TEL} className="btn btn-outline-on-dark">
            Call {PHONE_DISPLAY}
          </a>
        )}
      </div>
    </Section>
  )
}

/** Split hero: text left, photo right. */
export function SplitHero({ eyebrow, title, lede, image, imageAlt, actions }) {
  return (
    <section className="section-hero" style={{ background: 'var(--color-panel)' }}>
      <div className="container-site grid grid-2" style={{ gap: 48, alignItems: 'center' }}>
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h1 className="h1-hero" style={{ marginTop: 16 }}>{title}</h1>
          <p style={{ margin: '24px 0 0 0', maxWidth: 576, fontSize: 19, lineHeight: 1.7, color: 'var(--color-muted)' }}>
            {lede}
          </p>
          {actions && <div style={{ marginTop: 32, display: 'flex', flexWrap: 'wrap', gap: 16 }}>{actions}</div>}
        </div>
        <div style={{ minWidth: 0, overflow: 'hidden', borderRadius: 8, boxShadow: 'var(--shadow-hero)' }}>
          <img src={image} alt={imageAlt} className="hero-img" />
        </div>
      </div>
    </section>
  )
}

/** Non-medical disclaimer note (required on every service page and both intake forms). */
export function NonMedicalNote() {
  return (
    <div
      style={{
        borderRadius: 8,
        background: 'var(--color-panel-deep)',
        border: '1px solid var(--color-line)',
        padding: 24,
      }}
    >
      <p style={{ margin: 0, fontSize: 16, lineHeight: 1.6, color: 'var(--color-muted)' }}>{NON_MEDICAL_DISCLAIMER}</p>
    </div>
  )
}
